import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';
import { Clock } from 'lucide-react';

const LETTERS = ['A', 'B', 'C', 'D', 'E', 'F'];

export function TestTaker({ title, taker, questions, timeLimitMin, onSubmit }) {
  const [answers, setAnswers] = useState({});
  const [remaining, setRemaining] = useState((timeLimitMin || 0) * 60);
  const [submitting, setSubmitting] = useState(false);

  const doSubmit = async () => {
    setSubmitting(true);
    const payload = questions.map(q => ({ question_id: q.id, selected_index: answers[q.id] ?? -1 }));
    try { await onSubmit(payload); } finally { setSubmitting(false); }
  };

  useEffect(() => {
    if (!timeLimitMin) return;
    if (remaining <= 0) { toast.info("Time's up — submitting"); doSubmit(); return; }
    const t = setTimeout(() => setRemaining(r => r - 1), 1000);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [remaining, timeLimitMin]);

  const mm = String(Math.floor(remaining / 60)).padStart(2, '0');
  const ss = String(remaining % 60).padStart(2, '0');
  const answered = Object.keys(answers).length;

  return (
    <div className="max-w-2xl mx-auto space-y-4" data-testid="test-taker">
      <div className="flex items-center justify-between sticky top-0 bg-white/90 backdrop-blur py-2 z-10">
        <div>
          <h1 className="text-xl font-bold text-[#0F172A]">{title}</h1>
          {taker && <p className="text-xs text-slate-500">Candidate: {taker}</p>}
          <p className="text-xs text-slate-500">{answered}/{questions.length} answered</p>
        </div>
        {timeLimitMin ? <div className="flex items-center gap-1.5 text-sm font-mono font-semibold px-3 py-1.5 rounded-md bg-pink-50 text-pink-700 border border-pink-200" data-testid="test-timer"><Clock className="w-4 h-4" /> {mm}:{ss}</div> : null}
      </div>
      {questions.map((q, i) => (
        <Card key={q.id} className="border border-slate-200 shadow-none" data-testid={`test-q-${i}`}>
          <CardContent className="p-4">
            <p className="text-sm font-medium mb-2">{i + 1}. {q.text}</p>
            <div className="space-y-1.5">
              {q.options.map((o, oi) => (
                <label key={oi} className={`flex items-start gap-2 text-sm cursor-pointer rounded-md px-2 py-1.5 border transition-colors ${answers[q.id] === oi ? 'bg-pink-50 border-pink-300' : 'border-transparent hover:bg-slate-50'}`}>
                  <input type="radio" name={`q-${q.id}`} checked={answers[q.id] === oi} onChange={() => setAnswers(a => ({ ...a, [q.id]: oi }))} data-testid={`test-q-${i}-opt-${oi}`} className="mt-0.5" />
                  <span><b className="text-slate-500 mr-1">{LETTERS[oi]}.</b>{o}</span>
                </label>
              ))}
            </div>
          </CardContent>
        </Card>
      ))}
      <Button onClick={doSubmit} disabled={submitting} className="w-full bg-pink-600 hover:bg-pink-700 text-white" data-testid="test-submit">{submitting ? 'Submitting...' : 'Submit Test'}</Button>
    </div>
  );
}
